import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { ClientFetchService } from "@lib/helpers/clientFetchService";
import type {
    EnrichedOrderHistoryResponse,
    EnrichedOrderHistoryRequest,
} from "@lib/utils/order-history/types";
import { QueryKeys } from "@lib/enums/QueryKeys";
import { useStore } from "@store/index";
import { useLocale } from "next-intl";
import { useAuth } from "@lib/auth/useAuth";

const ORDERS_PER_PAGE = 10;

export function useOrderHistory() {
    const queryClient = useQueryClient();
    const locale = useLocale();
    const customer = useStore((state) => state.data.me.customer);
    const { isLoggedIn } = useAuth();

    return useInfiniteQuery({
        queryKey: [QueryKeys.getOrderHistoryDocument, "list"],
        queryFn: async ({ pageParam = 0 }) => {
            const response = await ClientFetchService.post<
                EnrichedOrderHistoryRequest,
                EnrichedOrderHistoryResponse
            >("/api/order-history", {
                pageOffset: pageParam,
                pageSize: ORDERS_PER_PAGE,
                locale,
            });

            // Populate individual order caches so they don't have to be refetched when accessed.
            if (response.orders && customer?.email) {
                response.orders.forEach((order) => {
                    const individualOrderResponse: EnrichedOrderHistoryResponse = {
                        ...response,
                        orders: [order],
                    };

                    queryClient.setQueryData(
                        [
                            QueryKeys.getOrderHistoryDocument,
                            order.orderNumber,
                            locale,
                            customer.email,
                        ],
                        individualOrderResponse
                    );
                });
            }

            return response;
        },
        getNextPageParam: (lastPage, allPages) => {
            if (!lastPage.thereMightBeMoreOrders) {
                return undefined;
            }

            const totalPagesFetched = allPages.reduce(
                (total, page) => total + (page.orders?.length ?? 0),
                0
            );
            return totalPagesFetched;
        },
        initialPageParam: 0,
        staleTime: 15 * 60 * 1000, // 15 minutes
        enabled: isLoggedIn && Boolean(customer?.email),
    });
}
